import { FC, useState } from 'react'

import { DropdownValue } from '@inno-ui/dropdown'
import {
  QuickFilterChips,
  QuickFilterChipsSelectedVariant
} from '@inno-ui/quick-filter-chips'
import { PLATFORM_NAMES } from 'modules/download-center/components/PlatformIcon'
import { useTranslation } from 'react-i18next'
import {
  DownloadPlatformOs,
  useGetDownloadableAppsQuery
} from 'shared/api/containerApi'

import styles from './DownloadCenterListPage.module.scss'
import { AppCard } from './components/AppCard'

type Filter = 'all' | DownloadPlatformOs

const PLATFORM_ORDER: DownloadPlatformOs[] = ['windows', 'mac', 'linux']

export const DownloadCenterListPage: FC = () => {
  const { t } = useTranslation()
  const { data: apps = [], isLoading } = useGetDownloadableAppsQuery()
  const [filter, setFilter] = useState<Filter>('all')

  const platformCounts = PLATFORM_ORDER.map(os => ({
    os,
    count: apps.filter(app => app.platforms.some(p => p.os === os)).length
  })).filter(({ count }) => count > 0)

  const filteredApps =
    filter === 'all'
      ? apps
      : apps.filter(app => app.platforms.some(p => p.os === filter))

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <img src='/Vector.svg' alt='' width={44} height={44} />
        <h1 className={styles.title}>{t('downloadCenter.listPage.title')}</h1>
        <p className={styles.subtitle}>
          Your licence covers all applications listed here. Install, sign in
          with your Clusterix account, and it uses your existing projects and
          files.
        </p>

        {!!apps.length && (
          <div className={styles.filters}>
            <QuickFilterChips
              items={[
                {
                  label: t('downloadCenter.listPage.filterAll'),
                  value: 'all',
                  count: apps.length
                },
                ...platformCounts.map(({ os, count }) => ({
                  label: PLATFORM_NAMES[os],
                  value: os,
                  count
                }))
              ]}
              value={filter}
              selectedVariant={QuickFilterChipsSelectedVariant.Soft}
              onChange={(value: DropdownValue) => {
                if (!value || Array.isArray(value)) return
                const nextFilter =
                  typeof value === 'string' ? value : value.value
                setFilter(nextFilter as Filter)
              }}
            />
          </div>
        )}
      </section>

      {isLoading && (
        <p className={styles.status}>{t('downloadCenter.listPage.loading')}</p>
      )}

      {!isLoading && !apps.length && (
        <p className={styles.status}>{t('downloadCenter.listPage.empty')}</p>
      )}

      <div className={styles.grid}>
        {filteredApps.map(app => (
          <AppCard key={app.id} app={app} />
        ))}
      </div>
    </div>
  )
}
